import { Close, CheckCircle } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Box, Divider, InputAdornment, TextField, Typography, useTheme, Alert } from "@mui/material";
import React, { memo, useState } from "react";
import { rupiah } from "../utils/currency";

interface CartItem {
  category_name: string;
  time: string;
  price: number;
  quantity: number;
}

interface EventDetails {
  include_tax: boolean;
  ppn: number;
  is_persen: boolean;
  fee: number;
  transaction_fee_is_persen: boolean;
  transaction_fee: number;
}

interface CartSummaryProps {
  filteredItems: CartItem[];
  totalTickets: number;
  totalPrice: number;
  voucher: string;
  error: string;
  discount: number;
  total: number;
  eventDetails: EventDetails;
  onVoucherChange: (value: string) => void;
  onRemoveVoucher: () => void;
  onApplyVoucher: () => void;
}

const CartSummary: React.FC<CartSummaryProps> = memo(
  ({
    filteredItems,
    totalTickets,
    voucher,
    error,
    discount,
    total,
    eventDetails,
    onVoucherChange,
    onRemoveVoucher,
    onApplyVoucher,
  }) => {
    const theme = useTheme();
    const [isHoveringIcon, setIsHoveringIcon] = useState(false);

    const calculateTax = () => eventDetails.include_tax ? 0 : (total * eventDetails.ppn) / 100;
    const calculatePlatformFee = () =>
      eventDetails.is_persen
        ? (total * eventDetails.fee) / 100
        : eventDetails.fee;
    const calculateTransactionFee = () =>
      eventDetails.transaction_fee_is_persen
        ? (total * eventDetails.transaction_fee) / 100
        : eventDetails.transaction_fee;
    const calculateGrandTotal = () =>
      Number(total) +
      Number(calculateTax()) +
      Number(calculatePlatformFee()) +
      Number(calculateTransactionFee());

    return (
      <Box
        sx={{
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: "8px",
          padding: "16px",
          backgroundColor: "background.paper",
          boxShadow: theme.shadows[1],
        }}
      >
        <Typography
          variant="h6"
          sx={{
            color: "primary.main",
            mb: 2,
            fontWeight: "bold",
          }}
        >
          Your Orders
        </Typography>

        {totalTickets > 0 ? (
          filteredItems.map((cart, key) => (
            <Box key={key} mb={2}>
              <Typography variant="body1" fontWeight="bold">
                {cart.category_name}
              </Typography>
              <Typography
                variant="body2"
                sx={{
                  color: "text.secondary",
                  mb: "4px",
                }}
              >
                {cart.time}
              </Typography>
              <Box display="flex" justifyContent="space-between">
                <Typography variant="body2" color="primary">
                  {rupiah(cart.price)}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  x {cart.quantity}
                </Typography>
              </Box>
            </Box>
          ))
        ) : (
          <Typography variant="body2" color="textSecondary" textAlign="center">
            Your cart is empty.
          </Typography>
        )}

        <Box mt={3}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
            gap={2}
          >
            <TextField
              fullWidth
              label="Voucher"
              name="voucher"
              value={voucher}
              onChange={(event) => onVoucherChange(event.target.value)}
              error={Boolean(error)}
              helperText={error}
              disabled={discount > 0}
              size="small"
              sx={{
                '& .MuiOutlinedInput-root': {
                  ...(discount > 0 && {
                    '& fieldset': {
                      borderColor: 'success.main',
                    },
                    '&:hover fieldset': {
                      borderColor: 'success.main',
                    },
                    '&.Mui-focused fieldset': {
                      borderColor: 'success.main',
                    },
                  }),
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {discount > 0 ? (
                      <Box
                        onMouseEnter={() => setIsHoveringIcon(true)}
                        onMouseLeave={() => setIsHoveringIcon(false)}
                        onClick={onRemoveVoucher}
                        sx={{
                          cursor: "pointer",
                          display: 'flex',
                          alignItems: 'center',
                          transition: 'all 0.2s ease-in-out',
                          '&:hover': {
                            transform: 'scale(1.1)'
                          }
                        }}
                      >
                        {isHoveringIcon ? (
                          <Close
                            color="error"
                            sx={{
                              transition: 'all 0.2s ease-in-out',
                            }}
                          />
                        ) : (
                          <CheckCircle
                            color="success"
                          />
                        )}
                      </Box>
                    ) : voucher && (
                      <Close
                        color="error"
                        sx={{ cursor: "pointer", "&:hover": { opacity: 0.8 } }}
                        onClick={onRemoveVoucher}
                      />
                    )}
                  </InputAdornment>
                ),
              }}
            />
            <LoadingButton
              variant="contained"
              onClick={onApplyVoucher}
              disabled={discount > 0}
              sx={{ height: "40px", minWidth: "80px" }}
            >
              {discount > 0 ? "APPLIED" : "USE"}
            </LoadingButton>
          </Box>

          <Divider sx={{ my: 2 }} />

          {discount > 0 && (
            <Box
              display="flex"
              justifyContent="space-between"
              mb={2}
              alignItems="center"
              sx={{
                p: 1,
                borderRadius: 1,
                border: '1px dashed',
                borderColor: 'success.main'
              }}
            >
              <Box display="flex" alignItems="center">
                <CheckCircle
                  color="success"
                  fontSize="small"
                  sx={{ mr: 1 }}
                />
                <Typography variant="body2" fontWeight="medium" color="success.main">
                  Voucher ({voucher})
                </Typography>
              </Box>
              <Typography
                variant="body2"
                sx={{ color: "success.main", fontWeight: "bold" }}
              >
                -{rupiah(discount)}
              </Typography>
            </Box>
          )}

          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography variant="body2" color="text.secondary">
              Sub Total
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {rupiah(total)}
            </Typography>
          </Box>

          {!eventDetails.include_tax && (
            <Box display="flex" justifyContent="space-between" mb={1}>
              <Typography variant="body2" color="text.secondary">
                Tax
              </Typography>
              <Typography variant="body2" fontWeight="bold">
                {rupiah(calculateTax())}
              </Typography>
            </Box>
          )}

          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography variant="body2" color="text.secondary">
              Platform Fee
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {rupiah(calculatePlatformFee())}
            </Typography>
          </Box>

          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography variant="body2" color="text.secondary">
              Transaction Fee
            </Typography>
            <Typography variant="body2" fontWeight="bold">
              {rupiah(calculateTransactionFee())}
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          <Box display="flex" justifyContent="space-between" mb={1}>
            <Typography variant="body2" color="text.secondary">
              Total
            </Typography>
            <Typography variant="body1" fontWeight="bold" color="primary">
              {rupiah(calculateGrandTotal())}
            </Typography>
          </Box>
        </Box>
      </Box>
    );
  }
);

export default CartSummary;